import * as React from 'react';
import propz from 'propz';
import { useHistory } from 'react-router-dom';
import { SendTimeFormEditor } from 'Src/components/SendTimeFormEditor/SendTimeFormEditor';
import { updateClubProgramme } from 'Src/helpers/service/admin/clubProgrammes';
import { getFormTemplates } from 'Src/helpers/service/admin/formTemplates';
import { validateFormDefinitionStructure } from 'Src/helpers/form/formDefinitionValidation';
import { uploadFormFile, uploadFormImage } from 'Src/helpers/form/formMedia';
import { formDefinitionToLegacyFields, legacyFieldsToFormDefinition } from 'Src/helpers/form/legacyFormAdapter';
import { AfterSchoolClubProgramme } from 'Src/models/clubProgramme';
import { ConfirmationRequestTemplate } from 'Src/models/confirmationRequestTemplate';
import { FormDefinition } from 'Src/types/formSchema';
import { AppUser } from 'Src/views/App/App';
import { ClubProgrammeFieldsList } from './components/ClubProgrammeFieldsList';

interface Props {
  user: AppUser;
  programme: AfterSchoolClubProgramme;
  onProgrammeUpdated: (p: AfterSchoolClubProgramme) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  mode?: 'SUMMARY' | 'FULL';
}

const getDefinition = (programme: AfterSchoolClubProgramme): FormDefinition =>
  Number(programme.formSchemaVersion) === 2 && programme.formDefinition
    ? programme.formDefinition
    : legacyFieldsToFormDefinition(propz.get(programme, ['defaultFields'], []), {
        title: `${programme.name || 'Club programme'} booking form`,
        description: programme.description || ''
      });

const normalizeDefinitionForCompare = (definition: FormDefinition): string =>
  JSON.stringify({
    schemaVersion: definition.schemaVersion || 2,
    title: definition.title || '',
    description: definition.description || '',
    theme: definition.theme || {},
    elements: definition.elements || [],
    logic: definition.logic || []
  });

const getTemplateDefinition = (
  template: ConfirmationRequestTemplate,
  programme: AfterSchoolClubProgramme
): FormDefinition | undefined =>
  template.formDefinition ||
  (Array.isArray(template.fields)
    ? legacyFieldsToFormDefinition(template.fields || [], {
        title: `${programme.name || 'Club programme'} booking form`
      })
    : undefined);

export const ClubProgrammeFieldsEditor: React.FC<Props> = ({
  user,
  programme,
  onProgrammeUpdated,
  onDirtyChange,
  mode = 'SUMMARY'
}) => {
  const history = useHistory();
  const canEdit = String(programme.status || 'DRAFT').toUpperCase() === 'DRAFT';
  const programmeId = String(programme.id || (programme as any)._id || '');
  const [definition, setDefinition] = React.useState<FormDefinition>(() => getDefinition(programme));
  const [savedSnapshot, setSavedSnapshot] = React.useState(() => JSON.stringify(getDefinition(programme)));
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [templates, setTemplates] = React.useState<ConfirmationRequestTemplate[]>([]);

  const isDirty = JSON.stringify(definition) !== savedSnapshot;
  const questionCount = definition.elements.filter(element => element.kind === 'QUESTION').length;
  const requiredCount = definition.elements.filter(element => element.kind === 'QUESTION' && element.required).length;
  const contentCount = definition.elements.filter(element => element.kind === 'CONTENT').length;
  const pageCount = Math.max(1, definition.elements.filter(element => element.type === 'PAGE_BREAK').length + 1);
  const currentDefinitionKey = normalizeDefinitionForCompare(definition);
  const matchedTemplate = templates.find(template => {
    const templateDefinition = getTemplateDefinition(template, programme);
    return templateDefinition && normalizeDefinitionForCompare(templateDefinition) === currentDefinitionKey;
  });
  const templateLabel = matchedTemplate
    ? `${matchedTemplate.name || 'Untitled template'}${matchedTemplate.isDefault ? ' (default)' : ''}`
    : 'Current programme form / custom';

  React.useEffect(() => {
    const next = getDefinition(programme);
    setDefinition(next);
    setSavedSnapshot(JSON.stringify(next));
    setError('');
  }, [String(programme.updatedAt || ''), String(programme.id || (programme as any)._id || '')]);

  React.useEffect(() => {
    getFormTemplates(user, 'CLUB_BOOKING')
      .then(setTemplates)
      .catch(() => undefined);
  }, [String(user.activeSchoolId || ''), String(user.roleSessionKey || '')]);

  React.useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (mode !== 'FULL' || !isDirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty, mode]);

  React.useEffect(() => {
    if (mode !== 'FULL') return;
    if (onDirtyChange) onDirtyChange(isDirty);
  }, [isDirty, mode, onDirtyChange]);

  React.useEffect(() => {
    return () => {
      if (onDirtyChange) onDirtyChange(false);
    };
  }, [onDirtyChange]);

  const openFullEditor = () => {
    if (!programmeId) return;
    history.push({
      pathname: `/clubProgrammes/${programmeId}/fields`,
      state: { from: { pathname: '/clubProgrammes', search: history.location.search || '' } }
    });
  };

  const save = () => {
    if (!canEdit || isSaving || !programmeId) return;

    const issues = validateFormDefinitionStructure(definition);
    if (issues.length > 0) {
      setError(issues.map(issue => issue.message).join(' '));
      return;
    }

    const invalidElement = definition.elements.find(element => {
      if (element.type === 'DIVIDER' || element.type === 'PAGE_BREAK') return false;
      return !String(element.title || '').trim();
    });
    if (invalidElement) {
      setError('Every question and content element must have a title.');
      return;
    }

    const fields = formDefinitionToLegacyFields(definition);
    setIsSaving(true);
    setError('');

    updateClubProgramme(
      user,
      {
        formSchemaVersion: 2,
        formDefinition: definition,
        defaultFields: fields as any
      },
      programmeId
    )
      .then((updated: AfterSchoolClubProgramme) => {
        const savedDefinition = updated?.formDefinition || definition;
        setDefinition(savedDefinition);
        setSavedSnapshot(JSON.stringify(savedDefinition));
        setIsSaving(false);
        if (updated) onProgrammeUpdated(updated);
      })
      .catch((err: any) => {
        setError(
          String(propz.get(err, ['response', 'data', 'details', 'text'], 'Failed to save the programme booking form.'))
        );
        setIsSaving(false);
      });
  };

  if (mode === 'SUMMARY') {
    const summaryFields = formDefinitionToLegacyFields(definition) as any;

    return (
      <section className="cpFieldsSummary">
        <div className="cpFieldsSummaryHeader">
          <div className="cpFieldsSummaryIcon" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M7 3h10a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" />
              <path d="M9 8h6M9 12h6M9 16h4" />
            </svg>
          </div>
          <div className="cpFieldsSummaryHeading">
            <div className="cpFieldsSummaryTitleRow">
              <h2>Booking form</h2>
              <span className={canEdit ? 'cpFieldsState isDraft' : 'cpFieldsState isLocked'}>
                {canEdit ? 'Draft' : 'Locked'}
              </span>
            </div>
            <p>Questions and information collected when a place is booked.</p>
          </div>
        </div>

        <div className="cpFieldsSummaryStats">
          <div>
            <strong>{questionCount}</strong>
            <span>{questionCount === 1 ? 'Question' : 'Questions'}</span>
          </div>
          <div>
            <strong>{contentCount}</strong>
            <span>{contentCount === 1 ? 'Content block' : 'Content blocks'}</span>
          </div>
          <div>
            <strong>v{Number(programme.formSchemaVersion) === 2 ? '2' : '1'}</strong>
            <span>Schema</span>
          </div>
        </div>

        <div className="cpFieldsTemplate">
          <span>Form source</span>
          <strong>{templateLabel}</strong>
        </div>

        {!canEdit ? (
          <div className="cpFieldsLockedNotice">
            <span aria-hidden="true">!</span>
            <p>This programme is no longer in Draft. Its booking form is locked to protect invitations and answers.</p>
          </div>
        ) : null}

        <div className="cpFieldsListHeader">
          <div>
            <h3>Form fields</h3>
            <p>{questionCount ? 'Review the questions in booking order.' : 'No questions have been added yet.'}</p>
          </div>
        </div>

        <div className="cpFieldsSummaryList">
          <ClubProgrammeFieldsList fields={summaryFields} />
        </div>

        <button type="button" className="cpFieldsEditorButton" onClick={openFullEditor} disabled={!programmeId}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            {canEdit ? (
              <path d="m4 16-.75 4.75L8 20l11-11-4-4L4 16Zm9-9 4 4" />
            ) : (
              <>
                <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
                <circle cx="12" cy="12" r="2.5" />
              </>
            )}
          </svg>
          {canEdit ? 'Manage form fields' : 'View complete form'}
        </button>
      </section>
    );
  }

  return (
    <div className="cpProgrammeFieldsEditor">
      <section className="cpProgrammeFieldsSummaryBar" aria-label="Booking form summary">
        <div className="cpProgrammeFieldsMetrics">
          <span>
            <strong>{questionCount}</strong> {questionCount === 1 ? 'question' : 'questions'}
          </span>
          <span>
            <strong>{requiredCount}</strong> required
          </span>
          <span>
            <strong>{pageCount}</strong> {pageCount === 1 ? 'page' : 'pages'}
          </span>
          <span className="cpProgrammeFieldsSource" title={templateLabel}>
            Source: <strong>{templateLabel}</strong>
          </span>
        </div>

        <div className="cpProgrammeFieldsSummaryActions">
          {isDirty ? <span className="cpProgrammeFieldsUnsaved">Unsaved changes</span> : null}
          {canEdit ? (
            <button
              type="button"
              className="cpProgrammeFieldsButton isPrimary"
              disabled={!isDirty || isSaving}
              onClick={save}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 4h12l2 2v14H5zM8 4v6h8V4M9 16h6" />
              </svg>
              {isSaving ? 'Saving…' : 'Save form'}
            </button>
          ) : null}
        </div>
      </section>

      {error ? (
        <div className="cpProgrammeFieldsError" role="alert">
          <span aria-hidden="true">!</span>
          <p>{error}</p>
        </div>
      ) : null}

      {!canEdit ? (
        <section className="cpProgrammeFieldsReadOnly">
          <div className="cpProgrammeFieldsLockedBanner">
            <span aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <rect x="5" y="10" width="14" height="10" rx="2" />
                <path d="M8 10V7a4 4 0 0 1 8 0v3" />
              </svg>
            </span>
            <div>
              <strong>This booking form is read-only</strong>
              <p>
                The programme is no longer in Draft. The form is locked to protect invitations and submitted answers.
              </p>
            </div>
          </div>
          <div className="cpProgrammeFieldsReadOnlyList">
            <ClubProgrammeFieldsList fields={formDefinitionToLegacyFields(definition) as any} />
          </div>
        </section>
      ) : (
        <div className="cpProgrammeFieldsBuilder">
          <SendTimeFormEditor
            definition={definition}
            onChange={next => {
              setDefinition(next);
              setError('');
            }}
            uploadImage={file => uploadFormImage(user, file)}
            uploadFile={file => uploadFormFile(user, file)}
            user={user}
            templateType="CLUB_BOOKING"
            initialTemplateId={matchedTemplate?.id}
            defaultTemplateName={`${programme.name || 'Club programme'} booking template`}
            defaultBuilderMode="COMPACT"
            showTemplateSaveAction={true}
            templateSaveButtonText="Save as club booking template"
            selectDefaultTemplateOnLoad={definition.elements.length === 0}
            applyDefaultTemplateOnLoad={true}
            emptyTemplateLabel="Start from scratch"
            emptyTemplateBuilderMode="COMPACT"
            showHelpAction={false}
          />
        </div>
      )}
    </div>
  );
};
